import {
  CatalogWithExternalEntitiesDto,
  CourseCopyPreviewDto,
  ExternalEntityDto,
  OsmosisTokenDto,
  UserDto
} from '../../apis/sherpath-course-management-service/sherpath-course-management-service.dtos';
import {
  CourseISBNDto,
  MasteryResetDto
} from '../../apis/eaq-app-facade-service/eaq-app-facade-service.dtos';
import { AppLinkData, } from '../../apis/eols-app-link/eols-app-link.dtos';
import { EvolveProductDto } from '../../apis/sherpath-app-facade-service/sherpath-app-facade-service.dtos';
import {
  CoursewareUserHistoryStateKey,
  EolsUserHistoryResponseDto
} from '../../apis/eols-user-crud/eols-user-crud.dtos';
import { CourseSectionDto } from '../../apis/eols-course-crud/eols-course-crud.dtos';
import {
  RecContentItemDto,
  RecTaxonomyNodeDto
} from '../../apis/rec-gateway/rec-gateway.dtos';
import { PrimaryTaxonomy } from '../../apis/rec-gateway/rec-gateway.models';
import { SyllabusItemDto } from '../../apis/sherpath-syllabus-service/sherpath-syllabus-service.dtos';
import {
  AssessmentDto,
  AssessmentSubmissionDto,
  AssignmentDto,
  AssignmentQuestionDto
} from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import { CrosswalkUserDto } from '../../apis/eols-user-management-service/eols-user-management-service.dtos';
import { ActiveSyllabusItemTypeDto } from '../../apis/sherpath-syllabus-service/sherpath-syllabus-service.constants';
import {
  FeatureFlagDto,
  FeatureFlagsGroupedDto
} from '../../apis/eols-features-api/eols-features-api.dtos';
import {
  contentCreateMethod,
  courseCreateMethod,
  sectionTitleMethod
} from '../../pages/course-builder/courseBuilder.constants';
import { EolsUserEngagementDto } from '../../apis/eols-user-engagement/eols-user-engagement.dtos';
import { AppLinkOutConfig } from '../../apis/eols-app-link/eols-app-link.models';
import { Messages } from '../../translations/message.models';
import {
  GroupActivityDto,
  SequenceMap
} from '../../apis/ocs-api-service/ocs-api-service.dtos';
import { MigratedCourseEntitlementDto } from '../../apis/sherpath-course-management-service/sherpath-course-management-service.utilities';
import { SyllabusFolderLocationInfo } from '../../pages/catalog/catalog.models';

export enum CourseBuilderField {
  CONTENT_CREATE_METHOD = 'CONTENT_CREATE_METHOD', // eslint-disable-line no-shadow
  COURSE_CREATE_METHOD = 'COURSE_CREATE_METHOD', // eslint-disable-line no-shadow
  COURSE_NAME = 'COURSE_NAME',
  CUSTOM_SECTION_TITLE = 'CUSTOM_SECTION_TITLE',
  END_DATE = 'END_DATE',
  FIRST_FOLDER_TITLE = 'FIRST_FOLDER_TITLE',
  IS_COURSE_LOCKED = 'IS_COURSE_LOCKED',
  IS_INCLUDE_DURATION_IN_TITLE = 'IS_INCLUDE_DURATION_IN_TITLE',
  NUMBER_OF_SECTIONS = 'NUMBER_OF_SECTIONS',
  SECTION_TITLE_METHOD = 'SECTION_TITLE_METHOD', // eslint-disable-line no-shadow
  SHOW_CONTENT = 'SHOW_CONTENT',
  START_DATE = 'START_DATE',
  TAXONOMY_IDS = 'TAXONOMY_IDS',
  SELECT_EXISTING_COURSE = 'SELECT_EXISTING_COURSE',
  COPY_COURSE_START_DATE = 'COPY_COURSE_START_DATE'
}

export interface CourseBuilderStore {
  isNewCourse: boolean;
  [CourseBuilderField.CONTENT_CREATE_METHOD]: contentCreateMethod;
  [CourseBuilderField.COURSE_CREATE_METHOD]: courseCreateMethod;
  [CourseBuilderField.COURSE_NAME]: string;
  [CourseBuilderField.CUSTOM_SECTION_TITLE]: string;
  [CourseBuilderField.END_DATE]: Date;
  [CourseBuilderField.FIRST_FOLDER_TITLE]: string;
  [CourseBuilderField.IS_COURSE_LOCKED]: boolean;
  [CourseBuilderField.IS_INCLUDE_DURATION_IN_TITLE]: boolean;
  [CourseBuilderField.NUMBER_OF_SECTIONS]: number;
  [CourseBuilderField.SECTION_TITLE_METHOD]: sectionTitleMethod;
  [CourseBuilderField.SHOW_CONTENT]: boolean;
  [CourseBuilderField.START_DATE]: Date;
  [CourseBuilderField.TAXONOMY_IDS]: string[];
  [CourseBuilderField.SELECT_EXISTING_COURSE]: string;
  [CourseBuilderField.COPY_COURSE_START_DATE]: Date;
  selectedTypes: ActiveSyllabusItemTypeDto[];
  courseCopyPreviewDto: CourseCopyPreviewDto;
  isDeepLinkCourseCopy: boolean;
}

export enum ReduxPage {
  SHERPATH_LESSON_EDITOR_PAGE = 'sherpathLessonEditorPage',
  EVOLVE_RESOURCES_EDITOR_PAGE = 'evolveResourcesEditorPage',
  SIMULATION_EDITOR_PAGE = 'simulationEditorPage',
  SKILL_EDITOR_PAGE = 'skillEditorPage',
  INTERACTIVE_REVIEW_EDITOR_PAGE = 'interactiveReviewEditorPage',
  INTERACTIVE_REVIEW_EDITOR_PREVIEW = 'interactiveReviewEditorPreview',
  EAQ_EDITOR_PAGE = 'eaqEditorPage',
  EBOOK_EDITOR_PAGE = 'ebookEditorPage',
  SHADOW_HEATH_EDITOR_PAGE = 'shadowHealthEditorPage',
  SHERPATH_POWERPOINT_EDITOR_PAGE = 'sherpathPowerpointEditorPage',
  SHERPATH_GROUP_ACTIVITY_EDITOR_PAGE = 'sherpathGroupActivityEditorPage',
  SHERPATH_CASE_STUDY_EDITOR_PAGE = 'sherpathCaseStudyEditorPage',
  OSMOSIS_VIDEO_EDITOR_PAGE = 'osmosisVideoEditorPage',
  COMMON_EDITOR_PAGE = 'commonEditorPage',
}
export enum FilterStateField {
  EBOOK_FILTER_STATE = 'ebookFilterState',
  SELECTED_PRODUCT = 'selectedProduct',
  RESOURCE_STATUS_FILTER = 'resourceStatusFilter',
  RESOURCE_TYPE_FILTER = 'resourceTypeFilter',
  RESOURCE_GRADING_FILTER = 'resourceGradingFilter',
  IS_HESI_FOCUS_CHAPTER_FILTER_ENABLED = 'isHesiFocusChapterFilterEnabled'
}

export interface AssignmentManagementStore {
  userEngagementReport: EolsUserEngagementDto[];
  userHistory: EolsUserHistoryResponseDto[];
  userHistoryStateCompletedRequests: CoursewareUserHistoryStateKey[];
  courseSectionId: string;
  currentCourse: CourseSectionDto;
  errors: Error[];
  evolveProducts: EvolveProductDto[];
  evolveUser: CrosswalkUserDto;
  featureFlagsGrouped: FeatureFlagsGroupedDto[];
  isbns: string;
  vantageIsbns: string[];
  isCourseOwner: boolean;
  linkData: AppLinkData;
  messages: Messages;
  pendingRequestCount: number;
  roleId: string;
  userCourseOwnerRecords: EolsUserHistoryResponseDto[];
  userId: string;
  users: UserDto[];
  eaqUsers: UserDto[];
  masteryResetSuccess: MasteryResetDto;
  eaqIsbns: CourseISBNDto[];
  eolsUser: UserDto;
  catalog: CatalogWithExternalEntitiesDto;
  primaryTaxonomies: PrimaryTaxonomy[];
  taxonomies: RecTaxonomyNodeDto[];
  assignment: AssignmentDto;
  assignments: AssignmentDto[];
  assessments: AssessmentDto[];
  assessmentSubmissionsMap: Record<string, AssessmentSubmissionDto[]>;
  checkedSyllabusItemIds: string[];
  syllabusFolderInfo: SyllabusFolderLocationInfo;
  courseBuilderState: CourseBuilderStore;
  externalEntities: ExternalEntityDto[];
  isBatchEditModeEnabled: boolean;
  isDragDropModeEnabled: boolean;
  syllabusItems: SyllabusItemDto[];
  interactiveReviewCatalogItem: RecContentItemDto;
  assignmentEditorCatalogItem: RecContentItemDto;
  assignmentEditorFoundAssignment: Partial<AssignmentDto>;
  crosswalkUsers: UserDto[];
  assessmentStartTimeMap: Record<string, Date>;
  registeredToken: string;
  appLinkCookies: {
    token: string;
    linkId: string;
  };
  collapsedFolderIds: string[];
  skillSubmissionRecords: EolsUserHistoryResponseDto[];
  skillStaticData: {
    data: string;
    id: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    moduleInfoList: any;
    vtwId: string;
  };
  [ReduxPage.SHERPATH_LESSON_EDITOR_PAGE]: {
    catalog: CatalogWithExternalEntitiesDto;
  };
  [ReduxPage.SKILL_EDITOR_PAGE]: {
    catalog: CatalogWithExternalEntitiesDto;
  };
  [ReduxPage.INTERACTIVE_REVIEW_EDITOR_PAGE]: {
    catalog: CatalogWithExternalEntitiesDto;
  };
  [ReduxPage.COMMON_EDITOR_PAGE]: {
    catalog: CatalogWithExternalEntitiesDto;
  };
  [ReduxPage.INTERACTIVE_REVIEW_EDITOR_PREVIEW]: {
    catalog: CatalogWithExternalEntitiesDto;
  };
  [ReduxPage.EAQ_EDITOR_PAGE]: {
    catalog: CatalogWithExternalEntitiesDto;
  };
  [ReduxPage.EVOLVE_RESOURCES_EDITOR_PAGE]: {
    catalog: CatalogWithExternalEntitiesDto;
  };
  [ReduxPage.SHADOW_HEATH_EDITOR_PAGE]: {
    catalog: CatalogWithExternalEntitiesDto;
  };
  [ReduxPage.SIMULATION_EDITOR_PAGE]: {
    catalog: CatalogWithExternalEntitiesDto;
  };
  [ReduxPage.EBOOK_EDITOR_PAGE]: {
    primaryTaxonomies: PrimaryTaxonomy[];
  };
  [ReduxPage.SHERPATH_POWERPOINT_EDITOR_PAGE]: {
    catalog: CatalogWithExternalEntitiesDto;
  };
  [ReduxPage.SHERPATH_GROUP_ACTIVITY_EDITOR_PAGE]: {
    catalog: CatalogWithExternalEntitiesDto;
  };
  [ReduxPage.SHERPATH_CASE_STUDY_EDITOR_PAGE]: {
    catalog: CatalogWithExternalEntitiesDto;
  };
  [ReduxPage.OSMOSIS_VIDEO_EDITOR_PAGE]: {
    catalog: CatalogWithExternalEntitiesDto;
  };
  batchEditSelectedSyllabusItems: SyllabusItemDto[];
  batchEditUpdatedSyllabusItems: SyllabusItemDto[];
  moduleSequenceMap: SequenceMap;
  enableDeepLink: boolean;
  groupActivity: GroupActivityDto;
  hasRunAuthessHealthCheck: boolean;
  osmosisTokenDto: OsmosisTokenDto;
  migratedEntitlements: MigratedCourseEntitlementDto[];
  slsQuizQuestions: AssignmentQuestionDto[];
  abTestFlavors: FeatureFlagDto[];
  importSelection: Partial<AssignmentDto>;
  lessonTopics: [];
}

export type ReduxPageWithCatalog = Extract<ReduxPage,
  ReduxPage.SKILL_EDITOR_PAGE |
  ReduxPage.INTERACTIVE_REVIEW_EDITOR_PAGE |
  ReduxPage.INTERACTIVE_REVIEW_EDITOR_PREVIEW |
  ReduxPage.EAQ_EDITOR_PAGE |
  ReduxPage.SIMULATION_EDITOR_PAGE |
  ReduxPage.SHERPATH_LESSON_EDITOR_PAGE |
  ReduxPage.EVOLVE_RESOURCES_EDITOR_PAGE |
  ReduxPage.SHADOW_HEATH_EDITOR_PAGE |
  ReduxPage.SHERPATH_POWERPOINT_EDITOR_PAGE |
  ReduxPage.SHERPATH_GROUP_ACTIVITY_EDITOR_PAGE |
  ReduxPage.SHERPATH_CASE_STUDY_EDITOR_PAGE |
  ReduxPage.OSMOSIS_VIDEO_EDITOR_PAGE |
  ReduxPage.COMMON_EDITOR_PAGE
>

export type ReduxPageWithPrimaryTaxonomies = Extract<ReduxPage, ReduxPage.EBOOK_EDITOR_PAGE>;

export type NavigateToApp = (config: AppLinkOutConfig, isNewTab?: boolean, token?: string) => void;

export type FilterStateType = Extract<FilterStateField,
  FilterStateField.EBOOK_FILTER_STATE |
  FilterStateField.RESOURCE_GRADING_FILTER |
  FilterStateField.RESOURCE_STATUS_FILTER |
  FilterStateField.RESOURCE_TYPE_FILTER |
  FilterStateField.SELECTED_PRODUCT |
  FilterStateField.IS_HESI_FOCUS_CHAPTER_FILTER_ENABLED>;

export type AdobeAnalyticsPageData = {
  visitor: {
    accessType: string;
    loginStatus: string;
    loginSuccess: string;
    loginFailure: string;
    accountId: string;
    accountName: string;
    ipAddress: string;
    userId: string;
    departmentId: string;
    departmentName: string;
    usagePathInfo: string;
  };
  page: {
    businessUnit: string;
    environment: string;
    productName: string;
    language: string;
    name: string;
    type: string;
    testId: string;
    channel: string;
    loadTime: number;
    loadTimestamp: string;
    analyticsPagename: string;
    sectionName: string;
    lastTrackedPage: string;
    cmsName: string;
    noTracking: string;
    eventName: string;
  };
  education: {
    courseId: string;
    courseName: string;
    isbn: string;
    assignmentId: string;
    assignmentName: string;
    assignmentType: string;
    moduleId: string;
    moduleName: string;
    moduleType: string;
    studentId: string;
    instructorId: string;
    semester: string;
    beforeAfterDueDate: string; // date compared to assignment due date in the format "due date +||- Int"
    assignmentNumericGrade: string;
    assignmentDuration: string;
    assignmentScore: string;
  };
}

export enum AdobeAnalyticsAction {
  ASSIGNMENT_CREATED = 'assignmentCreated',
  ASSIGNMENT_STARTED = 'assignmentStarted',
  ASSESSMENT_STARTED = 'assessmentStarted',
  ASSIGNMENT_COMPLETED = 'assignmentCompleted',
  ASSESSMENT_COMPLETED = 'assessmentCompleted',
}

export type AdobeAnalyticsActionProps = {
  action: AdobeAnalyticsAction.ASSIGNMENT_CREATED
    | AdobeAnalyticsAction.ASSIGNMENT_STARTED
    | AdobeAnalyticsAction.ASSIGNMENT_COMPLETED;
  pageData: {
    education: {
      assignmentId: string;
      assignmentName: string;
      assignmentType: string;
      beforeAfterDueDate: string;
    };
  };
} | {
  action: AdobeAnalyticsAction.ASSESSMENT_STARTED
    | AdobeAnalyticsAction.ASSESSMENT_COMPLETED;
  pageData: {
    education: {
      assignmentType: string;
    };
  };
}
